import { createTheme } from '@material-ui/core'
import { Theme, ThemeOptions } from '@material-ui/core/styles/createTheme'
import { greyPalette } from './grey.palette'

const primaryColor = '#09293E'
const secondaryColor = '#f44336'
const textPrimaryColor = '#231f20'
const whiteColor = '#ffffff'
const errorColor = '#fa0040'
const greyColor = '#9b9b9b'
const successColor = '#2ecc71'
const infoColor = '#4bd5f5'
const warningColor = '#ff8200'
const backdropColor = 'rgba(9, 41, 62, 0.8)'

export const getPixelInREM = (pixelSize: number): string => {
    return `${pixelSize / 16}rem`
}

export const greyTextFieldStyles = (theme: Theme) => ({
    textField: {
        borderBottom: `1px solid ${greyColor}`,
        fontWeight: 500,
        fontSize: getPixelInREM(16),
        color: textPrimaryColor
    },
    input: {
        '&:before': {
            borderBottom: 'none'
        },
        '&:after': {
            borderBottom: 'none'
        }
    }
})

export const getResponsiveWidth = (
    theme: Theme,
    desktop: string,
    tablet: string,
    mobile: string
) => ({
    [theme.breakpoints.down('sm')]: {
        width: mobile
    },
    [theme.breakpoints.between('sm', 'md')]: {
        width: tablet
    },
    [theme.breakpoints.up('md')]: {
        width: desktop
    }
})

declare module '@material-ui/core/styles/createTheme' {
    interface Theme {
        custom: {
            headerHeight: number
            colors: {
                oxfordBlue: React.CSSProperties['color']
                chartreuseGreen: React.CSSProperties['color']
                darkGrey: React.CSSProperties['color']
                backgrounds: {
                    hard: React.CSSProperties['color']
                    soft: React.CSSProperties['color']
                }
                white: React.CSSProperties['color']
                red: React.CSSProperties['color']
                blueGrayLight: React.CSSProperties['color']
                lightGrayBackground: React.CSSProperties['color']
            }
        }
    }
    interface ThemeOptions {
        custom?: {
            headerHeight?: number
            colors?: {
                oxfordBlue: React.CSSProperties['color']
                chartreuseGreen: React.CSSProperties['color']
                darkGrey: React.CSSProperties['color']
                backgrounds: {
                    hard: React.CSSProperties['color']
                    soft: React.CSSProperties['color']
                }
                white: React.CSSProperties['color']
                red: React.CSSProperties['color']
                blueGrayLight: React.CSSProperties['color']
                lightGrayBackground: React.CSSProperties['color']
            }
        }
    }
}

const createCustomTheme = (options: ThemeOptions) => {
    return createTheme({
        ...options
    })
}

const materialTheme = createCustomTheme({
    typography: {
        fontFamily: 'Roboto, Barlow, Helvetica, Arial, sans-serif'
    },
    palette: {
        primary: {
            main: primaryColor
        },
        secondary: {
            main: secondaryColor
        },
        error: {
            main: errorColor
        },
        success: {
            main: successColor
        },
        info: {
            main: infoColor
        },
        warning: {
            main: warningColor
        },
        text: {
            primary: textPrimaryColor
        },
        grey: greyPalette
    },
    overrides: {
        MuiButton: {
            root: {
                height: getPixelInREM(40),
                borderRadius: getPixelInREM(6),
                lineHeight: getPixelInREM(17),
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            },
            startIcon: {
                '& svg': {
                    width: getPixelInREM(14),
                    height: getPixelInREM(14)
                }
            },
            label: {
                fontWeight: 600
            },
            outlined: {
                border: `${getPixelInREM(2)} solid ${primaryColor}`,
                '&:hover': {
                    border: `${getPixelInREM(2)} solid ${primaryColor}`,
                    backgroundColor: 'transparent'
                },
                padding: `${getPixelInREM(11)} ${getPixelInREM(
                    15
                )} ${getPixelInREM(12)}`
            },
            outlinedPrimary: {
                border: `${getPixelInREM(2)} solid ${primaryColor}`,
                '&:hover': {
                    border: `${getPixelInREM(2)} solid ${primaryColor}`
                }
            },
            contained: {
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: primaryColor,
                    boxShadow: 'none'
                },
                padding: `${getPixelInREM(11)} ${getPixelInREM(
                    15
                )} ${getPixelInREM(12)}`
            },
            containedPrimary: {
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: primaryColor,
                    boxShadow: 'none'
                },
                '&:disabled': {
                    backgroundColor: primaryColor,
                    color: 'white',
                    opacity: 0.7,
                    pointerEvents: 'none'
                }
            },
            containedSecondary: {
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: secondaryColor,
                    boxShadow: 'none'
                },
                '&:disabled': {
                    backgroundColor: secondaryColor,
                    color: 'white',
                    opacity: 0.7,
                    pointerEvents: 'none'
                }
            }
        },
        MuiIconButton: {
            root: {
                '&:hover': {
                    backgroundColor: 'transparent'
                }
            }
        },
        MuiChip: {
            root: {
                borderRadius: getPixelInREM(6),
                color: 'white',
                fontWeight: 600,
                backgroundColor: greyPalette[600]
            }
        },
        MuiBackdrop: {
            root: {
                backgroundColor: backdropColor
            }
        }
    },
    props: {
        MuiButtonBase: {
            disableRipple: true
        },
        MuiIconButton: {
            disableRipple: true
        }
    },
    custom: {
        headerHeight: 60,
        colors: {
            oxfordBlue: '#09293e',
            chartreuseGreen: '#baf700',
            darkGrey: '#7f7f7f',
            backgrounds: {
                hard: '#E3E8E8',
                soft: '#FAF7F0'
            },
            white: whiteColor,
            red: secondaryColor,
            blueGrayLight: '#ebeff0',
            lightGrayBackground: 'rgb(245, 247, 248)'
        }
    }
})

export default materialTheme
